import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listAdminFishTypes, createFishType, updateFishType, setFishTypeActive } from '../../api/admin';
import { FishTypeFormDialog } from './FishTypeFormDialog';
import type { AdminFishTypeResponse } from '../../types/api';

export function AdminFishTypesTab() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminFishTypeResponse | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-fishtypes'],
    queryFn: listAdminFishTypes,
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) => setFishTypeActive(id, active),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-fishtypes'] }),
  });

  function openNew() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(ft: AdminFishTypeResponse) {
    setEditing(ft);
    setDialogOpen(true);
  }

  async function handleSave(name: string) {
    if (editing) {
      await updateFishType(editing.id, { name });
    } else {
      await createFishType({ name });
    }
    queryClient.invalidateQueries({ queryKey: ['admin-fishtypes'] });
  }

  if (isLoading) return <div className="p-4">Loading species…</div>;
  if (error) return <div className="p-4 text-red-500">Failed to load species.</div>;

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={openNew}
          className="rounded-md bg-teal-600 hover:bg-teal-700 px-4 py-2 text-sm font-medium text-white"
        >
          Add Species
        </button>
      </div>

      {!data || data.length === 0 ? (
        <div className="p-4 text-slate-500">No species.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Catches</th>
                <th className="px-3 py-2">Active</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {data.map(ft => (
                <tr key={ft.id} className={ft.active ? '' : 'opacity-50'}>
                  <td className="px-3 py-2 font-medium text-slate-900 dark:text-slate-100">{ft.name}</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{ft.catchCount}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => toggleActive.mutate({ id: ft.id, active: !ft.active })}
                      className={`rounded px-2 py-0.5 text-xs font-medium ${
                        ft.active
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                      }`}
                    >
                      {ft.active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => openEdit(ft)}
                      className="text-xs text-teal-600 hover:underline dark:text-teal-400"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FishTypeFormDialog
        open={dialogOpen}
        fishType={editing}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
      />
    </div>
  );
}
